/** Educational properties only; selection and geometry remain owned by Penpot. */
export function mountLearningAuthor({ getSection, resolveTarget, saveItems, run }) {
  const make = (tag, attributes = {}, children = []) => {
    const node = document.createElement(tag);
    for (const [key, value] of Object.entries(attributes)) {
      if (key === 'text') node.textContent = value;
      else node.setAttribute(key, value);
    }
    node.append(...children); return node;
  };
  const field = (name, input) => make('label', { class: 'es-work-field' }, [make('span', { text: name }), input]);
  const input = (name, max, multiline = false) => make(multiline ? 'textarea' : 'input', { 'aria-label': name, maxlength: max, ...(multiline ? { rows: '3' } : {}) });
  const action = (text, callback) => {
    const node = make('button', { type: 'button', text });
    node.addEventListener('click', () => { void run(callback); }); return node;
  };
  const heading = make('h2', { text: '레이어에 학습 연결' });
  const hint = make('p', { class: 'es-work-hint', text: '디자인에서 레이어를 선택해 연결하세요. 독자가 그 영역을 누르면 학습 활동이 열립니다.' });
  const unavailable = make('p', { class: 'es-work-hint', text: '디자인 페이지에서 레이어에 학습 활동을 연결할 수 있습니다.' });
  const targetName = make('p', { class: 'es-learning-target', role: 'status', text: '연결할 레이어를 선택하세요' });
  const title = input('학습 활동 제목', 200);
  const body = input('해설 내용', 4000, true);
  const url = input('영상 파일 주소', 2048); url.type = 'url'; url.placeholder = 'https://…/lesson.mp4';
  const question = input('확인 문제 질문', 2000, true);
  const options = input('선택지 · 한 줄에 하나', 3005, true);
  const answer = make('select', { 'aria-label': '정답 선택지' });
  const explanation = input('문제 해설', 4000, true);
  const kind = make('select', { 'aria-label': '학습 활동 종류' }, [
    make('option', { value: 'reveal', text: '해설 펼치기' }),
    make('option', { value: 'video', text: '영상 재생' }),
    make('option', { value: 'quiz', text: '확인 문제' }),
  ]);
  const revealFields = make('div', {}, [field('해설', body)]);
  const videoFields = make('div', {}, [field('영상 파일 주소', url), make('p', { class: 'es-work-hint', text: '브라우저에서 재생 가능한 MP4·WebM 파일의 HTTPS 주소를 넣으세요. 영상은 독자가 재생할 때 불러옵니다.' })]);
  const quizFields = make('div', {}, [field('질문', question), field('선택지 · 한 줄에 하나 (2~6개)', options), field('정답', answer), field('문제 해설', explanation)]);
  const list = make('div', { class: 'es-learning-list', 'aria-label': '연결한 학습 활동' });
  let sectionId, target = null, editingId = null;
  const setKind = () => { revealFields.hidden = kind.value !== 'reveal'; videoFields.hidden = kind.value !== 'video'; quizFields.hidden = kind.value !== 'quiz'; };
  const optionTexts = () => options.value.split('\n').map(text => text.trim()).filter(Boolean);
  function updateAnswers() {
    const previous = answer.value;
    answer.replaceChildren(...optionTexts().map((text, index) => make('option', { value: String(index), text: `${index + 1}. ${text}` })));
    if ([...answer.options].some(option => option.value === previous)) answer.value = previous;
  }
  function reset() {
    target = null; editingId = null; targetName.textContent = '연결할 레이어를 선택하세요';
    for (const element of [title, body, url, question, options, explanation]) element.value = '';
    kind.value = 'reveal'; updateAnswers(); setKind();
  }
  const bind = action('선택한 레이어 연결', async () => {
    const section = getSection();
    const value = await resolveTarget();
    if (getSection() !== section) throw new Error('페이지가 바뀌었습니다. 현재 페이지에서 레이어를 다시 선택하세요.');
    const { name, ...reference } = value;
    target = reference; targetName.textContent = `연결 대상: ${name}`;
    if (!title.value.trim()) title.value = name.slice(0, 200);
  });
  const save = action('학습 연결 저장', async () => {
    const section = getSection();
    if (section?.kind !== 'penpot' || !target) throw new Error('디자인에서 레이어를 선택한 뒤 연결해 주세요.');
    const item = { id: editingId ?? crypto.randomUUID(), kind: kind.value, title: title.value.trim(), target: structuredClone(target) };
    if (item.kind === 'reveal') item.body = body.value.trim();
    else if (item.kind === 'video') item.url = url.value.trim();
    else Object.assign(item, { question: question.value.trim(), options: optionTexts(), answerIndex: Number(answer.value), explanation: explanation.value.trim() });
    const items = [...(section.learning ?? [])];
    const index = items.findIndex(value => value.id === item.id);
    if (index < 0) items.push(item); else items[index] = item;
    await saveItems(items); reset(); refresh();
  });
  const form = make('div', { class: 'es-learning-form' }, [hint, bind, targetName, field('활동 제목', title), field('활동', kind), revealFields, videoFields, quizFields,
    make('div', { class: 'es-work-row' }, [save, action('새 연결', reset)]),
    make('p', { class: 'es-work-hint', text: '학습 연결 저장 후 독자 보기에서 확인하세요. 디자인을 수정하면 미리보기를 갱신할 때 위치도 반영됩니다.' }), list]);
  const element = make('div', { class: 'es-learning-author', 'data-testid': 'studio-learning-author' }, [heading, unavailable, form]);
  kind.addEventListener('change', setKind); options.addEventListener('input', updateAnswers);
  function refresh() {
    const section = getSection();
    if (sectionId !== section?.id) { reset(); sectionId = section?.id; }
    form.hidden = section?.kind !== 'penpot'; unavailable.hidden = !form.hidden;
    const items = section?.learning ?? [];
    list.replaceChildren(make('h3', { text: `연결한 활동 ${items.length}` }), ...items.map(item => {
      const edit = action('수정', () => {
        editingId = item.id; target = structuredClone(item.target); title.value = item.title; kind.value = item.kind;
        targetName.textContent = `연결 대상: ${item.title}`;
        body.value = item.kind === 'reveal' ? item.body : ''; url.value = item.kind === 'video' ? item.url : '';
        question.value = item.kind === 'quiz' ? item.question : ''; options.value = item.kind === 'quiz' ? item.options.join('\n') : '';
        explanation.value = item.kind === 'quiz' ? item.explanation : ''; updateAnswers();
        if (item.kind === 'quiz') answer.value = String(item.answerIndex);
        setKind(); title.focus();
      });
      edit.setAttribute('aria-label', `${item.title} 수정`);
      const remove = action('삭제', async () => {
        await saveItems((getSection().learning ?? []).filter(value => value.id !== item.id));
        if (editingId === item.id) reset(); refresh();
      });
      remove.setAttribute('aria-label', `${item.title} 삭제`);
      return make('article', {}, [make('strong', { text: item.title }), make('small', { text: { reveal: '해설', video: '영상', quiz: '확인 문제' }[item.kind] }), make('div', { class: 'es-work-row' }, [edit, remove])]);
    }));
  }
  refresh();
  return { element, refresh };
}
